import * as React from 'react';
import { cn } from '../../lib/utils';

/* ────────────────────────────────────────────────────────────────────────────
   FEAT-133 v9 redesign — typography primitives.

   These map directly to the v9 mockup tokens (.h-serif-lg, .h-label,
   .h-field-label, .h-title). Keep them in sync with mockups/webapp-redesign-v9.html
   if the design ever changes.
   ──────────────────────────────────────────────────────────────────────────── */

/* Serif page heading (Fraunces). Sizes mirror v9: 22 / 24 / 26 / 28 / 32.
   Use `as` to pick a different element (default h1). */
export const SerifHeading = React.forwardRef(function SerifHeading(
  { as: Component = 'h1', size = 'lg', className, children, ...rest },
  ref,
) {
  const sizes = {
    sm: 'text-[20px]',
    md: 'text-[22px]',
    lg: 'text-[24px]',
    xl: 'text-[26px]',
    '2xl': 'text-[28px]',
    '3xl': 'text-[32px]',
  };
  return (
    <Component
      ref={ref}
      className={cn(
        'font-heading font-medium text-slate-900 leading-[1.15] tracking-[-0.015em]',
        sizes[size] || sizes.lg,
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
});

/* Bold sans-serif title (used for tab page titles like "Your Hearths").
   v9 .h-title ≈ 22px / 700 / tracking -0.02em. `leading-none` collapses the
   text line-box to the glyph height so flex `items-center` alignment with
   adjacent icons (e.g. the Higlo logo in the header) actually lines up on
   the cap-height instead of the inflated line-box. */
export const PageTitle = React.forwardRef(function PageTitle(
  { className, children, ...rest },
  ref,
) {
  return (
    <h1
      ref={ref}
      className={cn(
        'text-[22px] font-bold tracking-[-0.02em] text-slate-900 leading-none',
        className,
      )}
      {...rest}
    >
      {children}
    </h1>
  );
});

/* Compact uppercase section label. .h-label in v9. */
export function SectionLabel({ as: Component = 'div', className, children, ...rest }) {
  return (
    <Component
      className={cn(
        'text-[11px] font-bold uppercase text-slate-500 tracking-[0.14em]',
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
}

/* Compact form-field label. .h-field-label in v9. */
export function FieldLabel({ htmlFor, className, children, ...rest }) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn('block text-[11px] font-semibold text-slate-600', className)}
      {...rest}
    >
      {children}
    </label>
  );
}

/* Body paragraph in muted slate. */
export function Lede({ className, children, ...rest }) {
  return (
    <p className={cn('text-[13px] leading-relaxed text-slate-500', className)} {...rest}>
      {children}
    </p>
  );
}
